import toast from 'react-hot-toast';
import { CheckCircle, AlertCircle, Info, Loader2, Heart, MessageCircle, UserPlus, ShoppingCart, Upload, Star } from 'lucide-react';

// Custom toast types with Ghanaian flair
export const showToast = {
  // Authentication toasts
  auth: {
    signInSuccess: () => toast.success('Akwaaba! Welcome back to PharmPlus! 🎉', {
      icon: '👋',
      duration: 3000,
      id: 'auth-signin-success'
    }),
    signUpSuccess: () => toast.success('Account created! Check your email to verify 📧', {
      icon: '✨',
      duration: 5000,
      id: 'auth-signup-success'
    }),
    signOutSuccess: () => toast.success('See you soon! Stay healthy! 👋', {
      icon: '🌟',
      duration: 2000,
      id: 'auth-signout-success'
    }),
    authError: (message: string) => toast.error(message, {
      icon: '🔐',
      duration: 4000,
      id: 'auth-error'
    }),
    authRequired: (message: string) => toast.error(message, {
      icon: '🔒',
      duration: 3000,
      id: 'auth-required',
      style: {
        background: '#fef3c7',
        color: '#92400e',
        border: '1px solid #fbbf24',
      },
    }),
    verificationEmailSent: () => toast.success('Verification email sent! Please check your inbox', {
      icon: '📧',
      duration: 5000,
      id: 'auth-verification-sent'
    }),
    passwordResetSent: () => toast.success('Password reset instructions sent to your email', {
      icon: '🔑',
      duration: 5000,
      id: 'auth-password-reset'
    }),
  },

  // Post & Community toasts
  community: {
    postCreated: () => toast.success('Your voice has been shared with the community! 📢', {
      icon: '💬',
      duration: 3000,
      id: 'community-post-created'
    }),
    postCreating: () => toast.loading('Sharing your thoughts...', {
      icon: '✍️',
      id: 'community-post-creating'
    }),
    commentAdded: () => toast.success('Comment added to the conversation! 💭', {
      icon: '💬',
      duration: 2000,
      id: 'community-comment-added'
    }),
    reactionAdded: (type: string) => {
      const reactionEmojis = {
        helpful: '💪',
        heart: '❤️',
        thanks: '🙏',
        strong: '💪',
        wow: '😮'
      };
      const emoji = reactionEmojis[type as keyof typeof reactionEmojis] || '👍';
      return toast.success(`Reaction added! ${emoji}`, {
        icon: emoji,
        duration: 1500,
        id: 'community-reaction-added'
      });
    },
    followSuccess: (name: string) => toast.success(`Now following ${name}! 🤝`, {
      icon: '👥',
      duration: 2000,
      id: 'community-follow-success'
    }),
    unfollowSuccess: (name: string) => toast.success(`Unfollowed ${name}`, {
      icon: '👋',
      duration: 2000,
      id: 'community-unfollow-success'
    }),
    pollVoteAdded: () => toast.success('Your vote has been counted! 📊', {
      icon: '🗳️',
      duration: 2000,
      id: 'community-poll-vote-added'
    }),
    pollVoteUpdated: () => toast.success('Your vote has been updated! 📊', {
      icon: '🔄',
      duration: 2000,
      id: 'community-poll-vote-updated'
    }),
    postShared: () => toast.success('Post shared successfully! 🔗', {
      icon: '🔗',
      duration: 2000,
      id: 'community-post-shared'
    }),
    topicFollowed: (topic: string) => toast.success(`Now following ${topic} topic! 📚`, {
      icon: '🔔',
      duration: 2000,
      id: 'community-topic-followed'
    }),
    postSaved: () => toast.success('Post saved to your bookmarks! 📑', {
      icon: '🔖',
      duration: 2000,
      id: 'community-post-saved'
    }),
    postReported: () => toast.success('Thank you for your report. Our team will review it.', {
      icon: '🛡️',
      duration: 3000,
      id: 'community-post-reported'
    }),
  },

  // Profile & Settings toasts
  profile: {
    updated: () => toast.success('Profile updated successfully! ✨', {
      icon: '👤',
      duration: 2000,
      id: 'profile-updated'
    }),
    updating: () => toast.loading('Updating your profile...', {
      icon: '⚙️',
      id: 'profile-updating'
    }),
    themeChanged: (theme: string) => toast.success(`Theme changed to ${theme}! 🎨`, {
      icon: '🌈',
      duration: 2000,
      id: 'profile-theme-changed'
    }),
    avatarUpdated: () => toast.success('Profile picture updated successfully!', {
      icon: '📸',
      duration: 2000,
      id: 'profile-avatar-updated'
    }),
    passwordChanged: () => toast.success('Password changed successfully! 🔐', {
      icon: '🔒',
      duration: 3000,
      id: 'profile-password-changed'
    }),
    emailChanged: () => toast.success('Email updated successfully! Please check your inbox for verification', {
      icon: '✉️',
      duration: 3000,
      id: 'profile-email-changed'
    }),
    notificationSettingsUpdated: () => toast.success('Notification preferences updated!', {
      icon: '🔔',
      duration: 2000,
      id: 'profile-notifications-updated'
    }),
    privacySettingsUpdated: () => toast.success('Privacy settings updated!', {
      icon: '🛡️',
      duration: 2000,
      id: 'profile-privacy-updated'
    }),
    accountDeleted: () => toast.success('Your account has been deleted. We\'re sorry to see you go!', {
      icon: '👋',
      duration: 5000,
      id: 'profile-account-deleted'
    }),
  },

  // Shopping & Orders toasts
  shop: {
    addedToCart: (productName: string) => toast.success(`${productName} added to cart! 🛒`, {
      icon: '🛍️',
      duration: 2000,
      id: 'shop-added-to-cart'
    }),
    removedFromCart: (productName: string) => toast.success(`${productName} removed from cart`, {
      icon: '🗑️',
      duration: 1500,
      id: 'shop-removed-from-cart'
    }),
    cartUpdated: () => toast.success('Cart updated! 📦', {
      icon: '✅',
      duration: 1500,
      id: 'shop-cart-updated'
    }),
    orderPlaced: (orderId: string) => toast.success(`Order #${orderId} placed successfully! 🎉`, {
      icon: '📦',
      duration: 4000,
      id: 'shop-order-placed'
    }),
    orderProcessing: () => toast.loading('Processing your order...', {
      icon: '💳',
      id: 'shop-order-processing'
    }),
    prescriptionUploaded: () => toast.success('Prescription uploaded successfully! 📄', {
      icon: '✅',
      duration: 3000,
      id: 'shop-prescription-uploaded'
    }),
    prescriptionUploading: () => toast.loading('Uploading prescription...', {
      icon: '📤',
      id: 'shop-prescription-uploading'
    }),
    prescriptionRequired: () => toast.error('This medication requires a prescription. Please upload one to continue.', {
      icon: '⚕️',
      duration: 4000,
      id: 'shop-prescription-required'
    }),
    outOfStock: (productName: string) => toast.error(`Sorry, ${productName} is currently out of stock.`, {
      icon: '⚠️',
      duration: 3000,
      id: 'shop-out-of-stock'
    }),
    orderShipped: (orderId: string) => toast.success(`Order #${orderId} has been shipped! 🚚`, {
      icon: '📦',
      duration: 4000,
      id: 'shop-order-shipped'
    }),
    orderDelivered: (orderId: string) => toast.success(`Order #${orderId} has been delivered! 📦`, {
      icon: '🎉',
      duration: 4000,
      id: 'shop-order-delivered'
    }),
    orderCancelled: (orderId: string) => toast.success(`Order #${orderId} has been cancelled.`, {
      icon: '❌',
      duration: 3000,
      id: 'shop-order-cancelled'
    }),
    confirmed: (orderId: string) => toast.success(`Order #${orderId} has been confirmed! 🎉`, {
      icon: '✅',
      duration: 4000,
      id: 'shop-order-confirmed'
    }),
    processing: (orderId: string) => toast.success(`Order #${orderId} is being processed.`, {
      icon: '⚙️',
      duration: 4000,
      id: 'shop-order-processing'
    }),
    shipped: (orderId: string) => toast.success(`Order #${orderId} has been shipped! 🚚`, {
      icon: '🚚',
      duration: 4000,
      id: 'shop-order-shipped'
    }),
    delivered: (orderId: string) => toast.success(`Order #${orderId} has been delivered! 🎉`, {
      icon: '🎁',
      duration: 4000,
      id: 'shop-order-delivered'
    }),
    paymentSuccessful: () => toast.success('Payment processed successfully! 💳', {
      icon: '✅',
      duration: 3000,
      id: 'shop-payment-successful'
    }),
    paymentFailed: () => toast.error('Payment processing failed. Please try again.', {
      icon: '❌',
      duration: 4000,
      id: 'shop-payment-failed'
    }),
  },

  // File upload toasts
  upload: {
    success: (fileName: string) => toast.success(`${fileName} uploaded successfully! 📁`, {
      icon: '✅',
      duration: 2000,
      id: 'upload-success'
    }),
    uploading: (fileName: string) => toast.loading(`Uploading ${fileName}...`, {
      icon: '📤',
      id: 'upload-uploading'
    }),
    error: (error: string) => toast.error(`Upload failed: ${error}`, {
      icon: '📁',
      duration: 4000,
      id: 'upload-error'
    }),
    fileTooLarge: (maxSize: number) => toast.error(`File is too large. Maximum size is ${maxSize}MB.`, {
      icon: '⚠️',
      duration: 4000,
      id: 'upload-file-too-large'
    }),
    invalidFileType: (acceptedTypes: string) => toast.error(`Invalid file type. Accepted types: ${acceptedTypes}`, {
      icon: '⚠️',
      duration: 4000,
      id: 'upload-invalid-file-type'
    }),
    progress: (progress: number) => toast.loading(`Uploading... ${progress}% complete`, {
      icon: '📤',
      id: 'upload-progress'
    }),
  },

  // Expert & Sessions toasts
  expert: {
    sessionJoined: (sessionName: string) => toast.success(`Joined "${sessionName}"! 🎓`, {
      icon: '🎯',
      duration: 3000,
      id: 'expert-session-joined'
    }),
    questionSubmitted: () => toast.success('Question submitted to expert! 🤔', {
      icon: '❓',
      duration: 3000,
      id: 'expert-question-submitted'
    }),
    expertFollowed: (expertName: string) => toast.success(`Now following Dr. ${expertName}! 👨‍⚕️`, {
      icon: '🩺',
      duration: 2000,
      id: 'expert-followed'
    }),
    verificationSubmitted: () => toast.success('Expert verification submitted! We\'ll review your application within 48 hours. 🩺', {
      icon: '📋',
      duration: 5000,
      id: 'expert-verification-submitted'
    }),
    contentCreated: (type: string) => toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} published successfully! 📚`, {
      icon: '✨',
      duration: 3000,
      id: 'expert-content-created'
    }),
    sessionReminder: (sessionName: string, timeLeft: string) => toast.success(`Reminder: "${sessionName}" starts in ${timeLeft}! 🔔`, {
      icon: '⏰',
      duration: 5000,
      id: 'expert-session-reminder'
    }),
    sessionStarting: (sessionName: string) => toast.success(`"${sessionName}" is starting now! Join to participate! 🎯`, {
      icon: '🔴',
      duration: 5000,
      id: 'expert-session-starting'
    }),
    verificationApproved: () => toast.success('Congratulations! Your expert verification has been approved! 🎉', {
      icon: '✅',
      duration: 5000,
      id: 'expert-verification-approved'
    }),
    verificationRejected: (reason: string) => toast.error(`Expert verification rejected: ${reason}. Please update and resubmit.`, {
      icon: '❌',
      duration: 5000,
      id: 'expert-verification-rejected'
    }),
  },

  // Chat & Messaging toasts
  chat: {
    messageSent: () => toast.success('Message sent! 💬', {
      icon: '📤',
      duration: 1500,
      id: 'chat-message-sent'
    }),
    messageReceived: (senderName: string) => toast.success(`New message from ${senderName}`, {
      icon: '📨',
      duration: 3000,
      id: 'chat-message-received'
    }),
    conversationStarted: (userName: string) => toast.success(`Started conversation with ${userName}! 💬`, {
      icon: '🗨️',
      duration: 2000,
      id: 'chat-conversation-started'
    }),
    expertMessageReceived: (expertName: string) => toast.success(`Dr. ${expertName} replied to your message! 👨‍⚕️`, {
      icon: '🩺',
      duration: 4000,
      id: 'chat-expert-message-received',
      style: {
        background: '#f0fdf4',
        color: '#166534',
        border: '1px solid #bbf7d0',
      },
    }),
    callStarted: (type: string, userName: string) => toast.success(`${type} call with ${userName} started`, {
      icon: type === 'Video' ? '📹' : '📞',
      duration: 3000,
      id: 'chat-call-started'
    }),
    callEnded: (duration: string) => toast.success(`Call ended. Duration: ${duration}`, {
      icon: '📞',
      duration: 3000,
      id: 'chat-call-ended'
    }),
    callMissed: (userName: string) => toast.error(`Missed call from ${userName}`, {
      icon: '📞',
      duration: 4000,
      id: 'chat-call-missed'
    }),
  },

  // General system toasts
  system: {
    loading: (message: string) => toast.loading(message, {
      icon: '⏳',
      id: 'system-loading'
    }),
    success: (message: string) => toast.success(message, {
      icon: '✅',
      duration: 2000,
      id: 'system-success'
    }),
    error: (message: string) => toast.error(message, {
      icon: '❌',
      duration: 4000,
      id: 'system-error'
    }),
    info: (message: string) => toast(message, {
      icon: 'ℹ️',
      duration: 3000,
      id: 'system-info',
      style: {
        background: '#eff6ff',
        color: '#1e40af',
        border: '1px solid #bfdbfe',
      },
    }),
    warning: (message: string) => toast(message, {
      icon: '⚠️',
      duration: 4000,
      id: 'system-warning',
      style: {
        background: '#fffbeb',
        color: '#d97706',
        border: '1px solid #fed7aa',
      },
    }),
    copied: (text: string) => toast.success(`"${text}" copied to clipboard!`, {
      icon: '📋',
      duration: 2000,
      id: 'system-copied'
    }),
    saved: (itemType: string) => toast.success(`${itemType} saved successfully!`, {
      icon: '💾',
      duration: 2000,
      id: 'system-saved'
    }),
    deleted: (itemType: string) => toast.success(`${itemType} deleted successfully!`, {
      icon: '🗑️',
      duration: 2000,
      id: 'system-deleted'
    }),
    updated: (itemType: string) => toast.success(`${itemType} updated successfully!`, {
      icon: '✏️',
      duration: 2000,
      id: 'system-updated'
    }),
    maintenance: (startTime: string, duration: string) => toast.info(`Scheduled maintenance: ${startTime} for ${duration}. Some features may be unavailable.`, {
      icon: '🔧',
      duration: 10000,
      id: 'system-maintenance'
    }),
  },

  // Network & connectivity toasts
  network: {
    offline: () => toast.error('You are offline. Some features may not work.', {
      icon: '📶',
      duration: 5000,
      id: 'network-status',
    }),
    online: () => toast.success('Back online! 🌐', {
      icon: '✅',
      duration: 2000,
      id: 'network-status',
    }),
    connectionError: () => toast.error('Connection error. Please try again.', {
      icon: '🔌',
      duration: 4000,
      id: 'network-connection-error'
    }),
    slowConnection: () => toast.warning('Slow network connection detected. Some features may be slower than usual.', {
      icon: '🐢',
      duration: 4000,
      id: 'network-slow'
    }),
    reconnecting: () => toast.loading('Reconnecting...', {
      icon: '🔄',
      id: 'reconnecting',
    }),
    reconnected: () => toast.success('Reconnected successfully!', {
      icon: '🔄',
      duration: 2000,
      id: 'reconnecting',
    }),
  },

  // Health-specific toasts with Ghanaian context
  health: {
    reminderSet: () => toast.success('Health reminder set! We\'ll keep you on track 💊', {
      icon: '⏰',
      duration: 3000,
      id: 'health-reminder-set'
    }),
    goalAchieved: (goal: string) => toast.success(`Congratulations! You achieved your ${goal} goal! 🎯`, {
      icon: '🏆',
      duration: 4000,
      id: 'health-goal-achieved'
    }),
    emergencyWarning: () => toast.error('For medical emergencies, please contact 193 or visit the nearest hospital immediately!', {
      icon: '🚨',
      duration: 8000,
      id: 'health-emergency'
    }),
    medicationReminder: (medication: string) => toast.info(`Time to take your ${medication}! 💊`, {
      icon: '⏰',
      duration: 5000,
      id: 'health-medication-reminder'
    }),
    appointmentReminder: (date: string, doctor: string) => toast.info(`Reminder: Appointment with ${doctor} on ${date}`, {
      icon: '📅',
      duration: 5000,
      id: 'health-appointment-reminder'
    }),
    healthTip: (tip: string) => toast.info(`Health Tip: ${tip}`, {
      icon: '💡',
      duration: 5000,
      id: 'health-tip'
    }),
  },
  
  // Form validation toasts
  form: {
    validationError: (field: string) => toast.error(`Please check the ${field} field and try again.`, {
      icon: '⚠️',
      duration: 3000,
      id: 'form-validation-error'
    }),
    requiredField: (field: string) => toast.error(`${field} is required.`, {
      icon: '⚠️',
      duration: 3000,
      id: 'form-required-field'
    }),
    invalidFormat: (field: string, format: string) => toast.error(`${field} must be in ${format} format.`, {
      icon: '⚠️',
      duration: 3000,
      id: 'form-invalid-format'
    }),
    formSubmitted: () => toast.success('Form submitted successfully!', {
      icon: '✅',
      duration: 2000,
      id: 'form-submitted'
    }),
    formSaved: () => toast.success('Form saved as draft!', {
      icon: '💾',
      duration: 2000,
      id: 'form-saved'
    }),
  },
  
  // Progress and achievement toasts
  progress: {
    milestone: (milestone: string) => toast.success(`Milestone achieved: ${milestone}! 🎉`, {
      icon: '🏆',
      duration: 4000,
      id: 'progress-milestone'
    }),
    streak: (days: number) => toast.success(`${days} day streak! Keep it up! 🔥`, {
      icon: '🔥',
      duration: 3000,
      id: 'progress-streak'
    }),
    levelUp: (level: number) => toast.success(`Congratulations! You've reached level ${level}! 🌟`, {
      icon: '⭐',
      duration: 4000,
      id: 'progress-level-up'
    }),
    badgeEarned: (badge: string) => toast.success(`New badge earned: ${badge}! 🏅`, {
      icon: '🏅',
      duration: 4000,
      id: 'progress-badge-earned'
    }),
    pointsEarned: (points: number, action: string) => toast.success(`+${points} points for ${action}! 🎯`, {
      icon: '🎯',
      duration: 2000,
      id: 'progress-points-earned'
    }),
  },

  // Payment toasts
  payment: {
    processing: () => toast.loading('Processing payment...', {
      icon: '💳',
      duration: 10000,
      id: 'payment-processing'
    }),
    success: () => toast.success('Payment successful! Thank you for your purchase.', {
      icon: '✅',
      duration: 3000,
      id: 'payment-success'
    }),
    failed: (reason?: string) => toast.error(`Payment failed${reason ? `: ${reason}` : ''}. Please try again.`, {
      icon: '❌',
      duration: 4000,
      id: 'payment-failed'
    }),
    cardAdded: () => toast.success('Payment card added successfully!', {
      icon: '💳',
      duration: 3000,
      id: 'payment-card-added'
    }),
    redirecting: () => toast.loading('Redirecting to payment gateway...', {
      icon: '🔄',
      duration: 5000,
      id: 'payment-redirecting'
    }),
  }
};

// Utility function to dismiss all toasts
export const dismissAllToasts = () => {
  toast.dismiss();
};

// Utility function to create custom toast with promise
export const toastPromise = <T,>(
  promise: Promise<T>,
  {
    loading,
    success,
    error,
  }: {
    loading: string;
    success: string | ((data: T) => string);
    error: string | ((error: any) => string);
  }
) => {
  return toast.promise(promise, {
    loading,
    success,
    error,
  });
};

// Utility function to create a toast with progress bar
export const toastProgress = (
  callback: (updateProgress: (progress: number) => void) => Promise<any>,
  {
    startMessage,
    progressMessage,
    successMessage,
    errorMessage,
  }: {
    startMessage: string;
    progressMessage: (progress: number) => string;
    successMessage: string;
    errorMessage: string;
  }
) => {
  const toastId = toast.loading(startMessage);
  
  let lastProgress = 0;
  
  const updateProgress = (progress: number) => {
    if (progress > lastProgress) {
      lastProgress = progress;
      toast.loading(progressMessage(progress), { id: toastId });
    }
  };
  
  callback(updateProgress)
    .then(() => {
      toast.success(successMessage, { id: toastId });
    })
    .catch((error) => {
      console.error('Operation failed:', error);
      toast.error(errorMessage, { id: toastId });
    });
    
  return toastId;
};

export default showToast;